import type { QueueResponse } from './ApiClient';
import type { ResponseError, Result } from './HttpClient';
import { JobRequestHandler } from './JobRequestHandler';
import type { JobRequestHandlerProps } from './JobRequestHandler';

/** A failed `Result` produced by the frontend itself rather than returned by the backend. */
export function errorResult(message: string, name = 'UnknownError', code = 500): ResponseError {
  return { ok: false, code, name, message, timestamp: new Date().toISOString() };
}

export interface RunJobOptions<T extends QueueResponse>
  extends Pick<JobRequestHandlerProps<T>, 'initFn' | 'statusFn' | 'signal' | 'pollingDelayMs'> {
  /** Logged, and used as the message of the failed `Result`, when the job throws without one. */
  fallbackMessage: string;
}

/**
 * Runs an orchestrator job (initial call, then status polling) to completion and resolves with
 * its `Result` — the promise shape `useApiCall` expects from an `apiFn`.
 *
 * An abort **rejects** with the `AbortError`, which `useApiCall` swallows; resolving a made-up
 * error instead would surface a cancelled request as a failure. Any other throw becomes a failed
 * `Result`.
 */
export function runJob<T extends QueueResponse>({
  fallbackMessage,
  ...job
}: RunJobOptions<T>): Promise<Result<T>> {
  return new Promise<Result<T>>((resolve, reject) => {
    let settled = false;

    const handler = new JobRequestHandler<T>({
      ...job,
      setResult: (result) => {
        if (settled) return;
        settled = true;
        resolve({ ...result, timestamp: new Date().toISOString() } as Result<T>);
      },
    });

    handler.start().catch((err) => {
      if (settled) return;
      settled = true;
      if ((err instanceof Error || err instanceof DOMException) && err.name === 'AbortError') {
        reject(err);
        return;
      }
      console.error(fallbackMessage, err);
      resolve(errorResult(err?.message || fallbackMessage) as Result<T>);
    });
  });
}
